import { orderBy } from 'lodash';
import {
  CmdError,
  CmdModal,
  CmdModalFooterButton,
  CmdRadioButton,
} from '@commander-services/gui-components';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { AxiosResponse } from 'axios';
import TableDatepicker from './TableDatepicker';
import LoaderService from '../../Services/LoaderService';
import { setLoaderAtom } from '../../store/recoil/AppState';
import { postRequest } from '../../Services/HttpService';
import { formatDuration } from '../RidePlayer/RidePlayerService';

interface SelectRidesModalProps {
  vehicleId: string;
  handleRide: (rideId: string, dates: Date[]) => void;
  closeCallback: () => void;
  defaultRideId?: string;
  defaultDates?: Date[];
}

interface SortByColumn {
  column: string;
  type: 'asc' | 'desc';
}

dayjs.extend(duration);

const COLUMN_DATE = 'startTs';
const COLUMN_DISTANCE = 'totalDistance';
const COLUMN_DURATION = 'totalDuration';
const COLUMN_DRIVER = 'driverName';
const COLUMN_MODE = 'private';
const ASC = 'asc';
const DESC = 'desc';

interface Ride {
  key: string;
  comment: null | string;
  customerId: number;
  driverId: number;
  driverName: string;
  ecoScore: number;
  endAddress: string;
  endTs: string;
  id: string;
  latEnd: number;
  latStart: number;
  lonEnd: number;
  lonStart: number;
  mileageEnd: number;
  mileageStart: number;
  parkingComment: null | string;
  parkingId: string;
  prevRideId: string;
  private: boolean;
  source: string;
  startAddress: string;
  startTs: string;
  totalDistance: number;
  totalDuration: number;
  vehicleId: number;
  isAnonymous: boolean;
}

export default function SelectRidesModal(props: SelectRidesModalProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const [rangeError, setRangeError] = React.useState(false);
  const isLoaderActive = useRecoilValue<boolean>(setLoaderAtom);
  const [sortByColumn, setSortByColumn] = React.useState<SortByColumn>({
    column: 'date',
    type: 'asc',
  });
  const [selectedRide, setSelectedRide] = React.useState('');
  const [sortedRides, setSortedRides] = React.useState<Ride[]>([]);
  const [defaultDates, setDefaultDates] = React.useState<Date[]>(props.defaultDates || []);

  React.useEffect(() => {
    const newSortedRides = orderBy(sortedRides, sortByColumn.column, sortByColumn.type);
    setSortedRides(newSortedRides);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortByColumn]);

  const handleSave = () => {
    if (selectedRide) {
      props.handleRide(selectedRide, defaultDates);
    }
  };

  const buttonsMoreRides: CmdModalFooterButton[] = [
    {
      id: 'close-select-rides-modal',
      type: 'button',
      title: f({ id: 'waypoint.confirm.dialog.cancel' }), // Vulgarny quickhack. V aktualnom case tejto zmeny, mi nesli zmenit preklady, lebo Anton tam ma limit na upload (440 nieco kb) a uz samotne preklady ho prekracuju
      className: 'e-button e-button--gray',
      closeCallback: props.closeCallback,
    },
    {
      id: 'submit-select-rides-modal',
      type: 'submit',
      title: f({ id: 'graph.velocty.modal.button.submit' }),
      className: 'e-button',
      submitCallback: handleSave,
    },
  ];

  const handleSort = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    const sortColumn = event.currentTarget.getAttribute('data-sort-column');
    const sortType = event.currentTarget.getAttribute('data-sort-type');
    if (sortColumn && sortType) {
      setSortByColumn((oldState: SortByColumn) => {
        const newState: SortByColumn = {
          ...oldState,
        };
        if (oldState.column !== sortColumn) {
          newState.column = sortColumn;
          newState.type = ASC;
        }
        if (oldState.column === sortColumn && sortType === ASC) {
          newState.type = DESC;
        }
        if (oldState.column === sortColumn && sortType === DESC) {
          newState.type = ASC;
        }
        return newState;
      });
    }
  };

  const getSortIconClass = (column: string): string => {
    if (sortByColumn && sortByColumn.column === column) {
      if (sortByColumn.type === ASC) {
        return 'cmd-icon-sorting-a-z text-active';
      }
      return 'cmd-icon-sorting-z-a text-active';
    }
    return 'cmd-icon-sorting-a-z text-gray';
  };

  const getSortType = (column: string): string => {
    if (column === sortByColumn.column) {
      return sortByColumn.type === ASC ? ASC : DESC;
    }
    return ASC;
  };

  const getLastVisibleRideId = (items: Ride[], key: number) => {
    if (items[key] && !items[key].isAnonymous) {
      return items[key]?.id;
    }
    if (key >= 0) {
      return getLastVisibleRideId(items, key - 1);
    }
    return '';
  };

  const getRides = async (dates?: Date[]) => {
    const url = `/v1/vehicle/${props.vehicleId}/ride/list`;
    const filter = {
      tsAllowlist:
        dates && dates.length === 2
          ? [
              {
                min: dates[0],
                max: dates[1],
              },
            ]
          : [
              {
                min: new Date(new Date().setHours(0, 0, 0, 0)),
                max: new Date(),
              },
            ],
    };
    LoaderService.showLoader(true);
    const response: AxiosResponse | false = await postRequest(`${url}`, filter, {
      returnAxiosResponse: true,
    });
    if (response && response.data && response.status === 200 && response.data.length > 0) {
      const data = response.data;
      data.map((i) => {
        i[COLUMN_DURATION] =
          i.endTs && i.startTs
            ? new Date(i.endTs).getTime() / 1000 - new Date(i.startTs).getTime() / 1000
            : null;
        i[COLUMN_DISTANCE] = i.mileageEnd && i.mileageStart ? i.mileageEnd - i.mileageStart : null;
        return i;
      });
      setSortedRides(data);
      let defaultRideId = '';
      if (props.defaultRideId && data.find((i) => i.id === props.defaultRideId)) {
        defaultRideId = props.defaultRideId;
      }
      setSelectedRide(
        defaultRideId || getLastVisibleRideId(response.data, response.data.length - 1)
      );
    } else {
      setSortedRides([]);
      setSelectedRide('');
    }
    LoaderService.showLoader(false);
  };

  React.useEffect(() => {
    if (props.vehicleId) {
      getRides(props.defaultDates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDatepicker = (value?: Date[] | null) => {
    if (!value) {
      setRangeError(true);
      setSortedRides([]);
      setSelectedRide('');
    } else {
      setRangeError(false);
      getRides(value);
      setDefaultDates(value);
    }
  };

  const handleSelectedRide = (value: string) => {
    setSelectedRide(value);
  };

  const getRideEnd = (rideData: Ride) => {
    if (rideData.endTs) {
      return [
        <span className="e-dot e-dot--sm e-dot--red" />,
        <span> </span>,
        dayjs(rideData.endTs).format('D.M.YYYY, H:mm:ss'),
      ];
    }
    if (rideData.isAnonymous) {
      return [
        <span className="e-dot e-dot--sm e-dot--red" />,
        <span> </span>,
        f({ id: 'tables.tripsOverview.column.datetime.anonymized' }),
      ];
    }
    return <br />;
  };

  const getDistance = (rideData: Ride) => {
    return rideData && rideData.totalDistance !== undefined && rideData.totalDistance !== null
      ? `${rideData.totalDistance.toFixed(2)} km`
      : '';
  };

  const getDuration = (rideData: Ride) => {
    if (rideData.isAnonymous) {
      return <i className="cmd-icons cmd-icon-gdpr-document-lock-dark-gray" />;
    }
    return rideData.totalDuration ? formatDuration(rideData.totalDuration) : '';
  };

  return (
    <CmdModal
      id="select-rides-modal"
      title={f({ id: 'graph.velocty.modal.title.rideChoosing' })}
      footerButtons={buttonsMoreRides}
      size="xl"
    >
      <div style={{ marginBottom: '10px' }}>
        <div
          data-cy="datapicker-popup-velocity-graph"
          className="label label-primary mb-xs position-relative label--filter"
          style={{ padding: '9px 8px 8px 8px' }}
        >
          <span>
            <TableDatepicker
              name="graph-velocity"
              type="chip"
              onChangeCallback={handleDatepicker}
              defaultDates={props.defaultDates}
              rideChoosing
            />
          </span>
        </div>
        {rangeError && (
          <div>
            <CmdError message={f({ id: 'errors.reports.cannotSelectDateLongerThanMonth' })} />
          </div>
        )}
      </div>

      <div className="b-select-ride-table">
        <div className="table-responsive height-auto">
          <table className="table table-striped table-bordered table-listings m-0">
            <thead>
              <tr>
                <th className="th-sticky-top w-auto text-center" style={{ minWidth: '40px' }}>
                  <span className="sr-only">{f({ id: 'table.selectRecords' })}</span>
                </th>
                <th
                  className="th-sticky-top"
                  title={f({ id: 'ridePlayer.selectRidesModal.table.dateAndTime.tooltip' })}
                >
                  {f({ id: 'ridePlayer.selectRidesModal.table.dateAndTime' })}
                  <button
                    id="sort-date"
                    title={f({
                      id: `table.filter.${getSortType(COLUMN_DATE).toLocaleLowerCase()}`,
                    })}
                    onClick={handleSort}
                    data-sort-column={COLUMN_DATE}
                    data-sort-type={getSortType(COLUMN_DATE)}
                    className="filter text-decoration-none position-relative ml-2"
                  >
                    <i className={`cmd-icon ${getSortIconClass(COLUMN_DATE)}`} />
                    <span className="sr-only">
                      {f({ id: `table.filter.${getSortType(COLUMN_DATE).toLocaleLowerCase()}` })}
                    </span>
                  </button>
                </th>
                <th
                  className="th-sticky-top"
                  style={{ textTransform: 'capitalize' }}
                  title={f({ id: 'ridePlayer.selectRidesModal.table.mode.tooltip' })}
                >
                  {f({ id: 'ridePlayer.selectRidesModal.table.mode' })}
                  <button
                    id="sort-mode"
                    title={f({
                      id: `table.filter.${getSortType(COLUMN_MODE).toLocaleLowerCase()}`,
                    })}
                    onClick={handleSort}
                    data-sort-column={COLUMN_MODE}
                    data-sort-type={getSortType(COLUMN_MODE)}
                    className="filter text-decoration-none position-relative ml-2"
                  >
                    <i className={`cmd-icon ${getSortIconClass(COLUMN_MODE)}`} />
                    <span className="sr-only">
                      {f({
                        id: `table.filter.${getSortType(COLUMN_MODE)}`.toLocaleLowerCase(),
                      })}
                    </span>
                  </button>
                </th>
                <th
                  className="th-sticky-top"
                  title={f({ id: 'ridePlayer.selectRidesModal.table.address.tooltip' })}
                >
                  {f({ id: 'ridePlayer.selectRidesModal.table.address' })}
                </th>
                <th
                  className="th-sticky-top"
                  title={f({ id: 'ridePlayer.selectRidesModal.table.distance.tooltip' })}
                >
                  {f({ id: 'ridePlayer.selectRidesModal.table.distance' })}
                  <button
                    id="sort-distance"
                    title={f({
                      id: `table.filter.${getSortType(COLUMN_DISTANCE).toLocaleLowerCase()}`,
                    })}
                    onClick={handleSort}
                    data-sort-column={COLUMN_DISTANCE}
                    data-sort-type={getSortType(COLUMN_DISTANCE)}
                    className="filter text-decoration-none position-relative ml-2"
                  >
                    <i className={`cmd-icon ${getSortIconClass(COLUMN_DISTANCE)}`} />
                    <span className="sr-only">
                      {f({
                        id: `table.filter.${getSortType(COLUMN_DISTANCE)}`.toLocaleLowerCase(),
                      })}
                    </span>
                  </button>
                </th>
                <th
                  className="th-sticky-top"
                  title={f({ id: 'ridePlayer.selectRidesModal.table.duration.tooltip' })}
                >
                  {f({ id: 'ridePlayer.selectRidesModal.table.duration' })}
                  <button
                    id="sort-duration"
                    title={f({
                      id: `table.filter.${getSortType(COLUMN_DURATION)}`.toLocaleLowerCase(),
                    })}
                    onClick={handleSort}
                    data-sort-column={COLUMN_DURATION}
                    data-sort-type={getSortType(COLUMN_DURATION)}
                    className="filter text-decoration-none position-relative ml-2"
                  >
                    <i className={`cmd-icon ${getSortIconClass(COLUMN_DURATION)}`} />
                    <span className="sr-only">
                      {f({
                        id: `table.filter.${getSortType(COLUMN_DURATION)}`.toLocaleLowerCase(),
                      })}
                    </span>
                  </button>
                </th>
                <th
                  className="th-sticky-top"
                  title={f({ id: 'tables.tripsOverview.header.tooltip.driver' })}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {f({ id: 'rides.driver' })}
                  <button
                    id="sort-drivername"
                    title={f({
                      id: `table.filter.${getSortType(COLUMN_DRIVER)}`.toLocaleLowerCase(),
                    })}
                    onClick={handleSort}
                    data-sort-column={COLUMN_DRIVER}
                    data-sort-type={getSortType(COLUMN_DRIVER)}
                    className="filter text-decoration-none position-relative ml-2"
                  >
                    <i className={`cmd-icon ${getSortIconClass(COLUMN_DRIVER)}`} />
                    <span className="sr-only">
                      {f({
                        id: `table.filter.${getSortType(COLUMN_DRIVER)}`.toLocaleLowerCase(),
                      })}
                    </span>
                  </button>
                </th>
              </tr>
            </thead>

            <tbody>
              {!isLoaderActive && sortedRides.length === 0 && (
                <tr className="hover-off border-off background-off">
                  <td colSpan={38} style={{ background: 'rgb(255, 255, 255)' }}>
                    <div className="text-center py-8 align-items-center align-items-center">
                      <div className="text-center py-8 align-items-center align-items-center">
                        <div className="my-8 py-8">
                          <div className="fs-7 text-gray-light">
                            {f({ id: 'graph.onlinepanel.noData' })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              {sortedRides.map((rideData: Ride) => {
                return (
                  <tr key={`select-ride-${rideData.key}`}>
                    <td
                      className="align-middle text-center justify-content-center b-table-prehlad-jazd-checkbox"
                      style={{ position: 'relative' }}
                    >
                      <div style={{ position: 'absolute', top: '8px', left: '10px' }}>
                        <CmdRadioButton
                          id={`ride-${rideData.id}`}
                          name={`ride-${rideData.id}`}
                          label=""
                          // label={f({ id: 'alarms.section.vehicles.forvehicles' })}
                          options={[{ value: String(rideData.id), item: '' }]}
                          value={String(selectedRide)}
                          setValue={handleSelectedRide}
                          disabled={rideData.isAnonymous}
                        />
                      </div>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span className="e-dot e-dot--sm e-dot--green" />{' '}
                      {dayjs(rideData.startTs).format('D.M.YYYY, H:mm:ss')}
                      <br />
                      {getRideEnd(rideData)}
                    </td>
                    <td className="text-center align-middle justify-content-center">
                      <i
                        className={`text-gray-lighten cmd-icon ${
                          rideData.private
                            ? 'cmd-icon-ride-mode-private'
                            : 'cmd-icon-ride-mode-work'
                        } `}
                        title={f({
                          id: rideData.private
                            ? 'enum.ridebooks.rideTypePrivate'
                            : 'enum.ridebooks.rideTypeBusiness',
                        })}
                      />
                      <span className="sr-only">
                        {f({
                          id: rideData.private
                            ? 'enum.ridebooks.rideTypePrivate'
                            : 'enum.ridebooks.rideTypeBusiness',
                        })}
                      </span>
                    </td>
                    <td
                      className="align-middle"
                      title={`${rideData.startAddress}\n${rideData.endAddress}`}
                    >
                      {rideData.isAnonymous ? (
                        f({ id: 'tables.tripsOverview.column.datetime.anonymized' })
                      ) : (
                        <div>
                          {rideData.startAddress}
                          <br />
                          {rideData.endAddress}
                        </div>
                      )}
                    </td>
                    <td className="align-middle text-right">{getDistance(rideData)}</td>
                    <td
                      className={`align-middle ${
                        rideData.isAnonymous ? 'text-center' : 'text-right'
                      }`}
                    >
                      {getDuration(rideData)}
                    </td>
                    <td className="align-middle">
                      {rideData.driverName || f({ id: 'enum.ridebooks.unassignedDriver' })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </CmdModal>
  );
}
